import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { publicPortalApi } from '@/lib/api/portal.js';
import { formatCurrency, formatDateTime } from '@/lib/format.js';
import { PublicShell } from './PortalTokenViewPage.js';
import { parseApiError } from '@/lib/api/errors.js';
import { toastError } from '@/lib/toast.js';

export function EstimateApprovePage() {
  const { token } = useParams<{ token: string }>();
  const [params] = useSearchParams();
  const preferDecline = params.get('action') === 'decline';

  const [name, setName] = useState('');
  const [reason, setReason] = useState('');
  const [done, setDone] = useState<'approved' | 'declined' | null>(null);
  const [ticketNumber, setTicketNumber] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);

  const estimateQuery = useQuery({
    queryKey: ['public-estimate', token],
    queryFn: () => publicPortalApi.getEstimate(token!),
    enabled: Boolean(token) && !done,
    retry: false,
  });

  const data = estimateQuery.data as {
    estimateNumber: string;
    deviceSummary: string;
    services: Array<{ name: string; price: number }>;
    parts: Array<{ name: string; price: number }>;
    subtotal: string;
    tax: string;
    total: string;
    notes: string | null;
    validUntil: string | null;
    storeName: string | null;
    logoUrl: string | null;
    requiresSignature: boolean;
  } | undefined;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';

    const pos = (e: PointerEvent) => {
      const rect = canvas.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const onDown = (e: PointerEvent) => {
      drawing.current = true;
      const p = pos(e);
      ctx.beginPath();
      ctx.moveTo(p.x, p.y);
    };
    const onMove = (e: PointerEvent) => {
      if (!drawing.current) return;
      const p = pos(e);
      ctx.lineTo(p.x, p.y);
      ctx.stroke();
    };
    const onUp = () => {
      drawing.current = false;
    };

    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointerleave', onUp);
    return () => {
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointerleave', onUp);
    };
  }, [data?.requiresSignature]);

  const approveMutation = useMutation({
    mutationFn: () => {
      const signatureDataUrl =
        data?.requiresSignature && canvasRef.current
          ? canvasRef.current.toDataURL('image/png')
          : undefined;
      return publicPortalApi.approveEstimate(token!, {
        approvedByName: name,
        signatureDataUrl,
      });
    },
    onSuccess: (res) => {
      setTicketNumber(res.ticketNumber);
      setDone('approved');
    },
    onError: (err) => {
      const parsed = parseApiError(err);
      toastError(parsed.message, parsed.code);
    },
  });

  const declineMutation = useMutation({
    mutationFn: () => publicPortalApi.declineEstimate(token!, { reason: reason || undefined }),
    onSuccess: () => setDone('declined'),
    onError: (err) => {
      const parsed = parseApiError(err);
      toastError(parsed.message, parsed.code);
    },
  });

  if (done === 'approved') {
    return (
      <PublicShell storeName={data?.storeName} logoUrl={data?.logoUrl}>
        <div className="rounded-2xl border border-green-200 bg-green-50 p-6 text-center">
          <h1 className="text-xl font-bold text-green-900">✓ Estimate Approved!</h1>
          <p className="mt-2 text-sm text-green-800">
            Thank you{name ? `, ${name}` : ''}. We&apos;ll begin your repair shortly.
            {ticketNumber ? ` (${ticketNumber})` : ''}
          </p>
          <p className="mt-2 text-sm text-green-700">
            You&apos;ll receive a message when it&apos;s ready.
          </p>
        </div>
      </PublicShell>
    );
  }

  if (done === 'declined') {
    return (
      <PublicShell storeName={data?.storeName} logoUrl={data?.logoUrl}>
        <div className="rounded-2xl border border-slate-200 bg-white p-6 text-center">
          <h1 className="text-xl font-bold">Estimate Declined</h1>
          <p className="mt-2 text-sm text-slate-600">
            We&apos;ve notified the shop. They may reach out with alternatives.
          </p>
        </div>
      </PublicShell>
    );
  }

  if (estimateQuery.isLoading) {
    return (
      <PublicShell>
        <p className="text-center text-slate-500">Loading estimate…</p>
      </PublicShell>
    );
  }

  if (estimateQuery.isError || !data) {
    return (
      <PublicShell>
        <p className="text-center text-slate-500">
          This estimate is unavailable or has expired.
        </p>
      </PublicShell>
    );
  }

  return (
    <PublicShell storeName={data.storeName ?? undefined} logoUrl={data.logoUrl}>
      <h1 className="text-center text-xl font-bold">Repair Estimate for Approval</h1>
      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="font-bold">{data.estimateNumber}</div>
        <p className="text-sm text-slate-600">{data.deviceSummary}</p>

        <h2 className="mt-4 text-xs font-bold uppercase text-slate-500">Services</h2>
        <ul className="mt-2 space-y-1 text-sm">
          {data.services.map((s) => (
            <li key={s.name} className="flex justify-between">
              <span>{s.name}</span>
              <span>{formatCurrency(s.price)}</span>
            </li>
          ))}
          {data.parts.map((p) => (
            <li key={p.name} className="flex justify-between text-slate-600">
              <span>{p.name}</span>
              <span>{formatCurrency(p.price)}</span>
            </li>
          ))}
        </ul>
        <div className="mt-3 flex justify-between border-t border-slate-100 pt-2 font-bold">
          <span>Total</span>
          <span>{formatCurrency(data.total)}</span>
        </div>

        {data.validUntil ? (
          <p className="mt-3 text-sm text-slate-500">
            This estimate is valid until: {formatDateTime(data.validUntil)}
          </p>
        ) : null}

        {data.notes ? (
          <div className="mt-3 rounded-xl bg-slate-50 p-3 text-sm text-slate-700">
            <div className="text-xs font-bold uppercase text-slate-500">Notes from technician</div>
            <p className="mt-1">{data.notes}</p>
          </div>
        ) : null}

        {preferDecline ? (
          <div className="mt-4 space-y-3">
            <label className="block text-sm font-medium">
              Reason (optional)
              <textarea
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </label>
            <button
              type="button"
              onClick={() => declineMutation.mutate()}
              className="w-full rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"
            >
              Confirm Decline
            </button>
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {data.requiresSignature ? (
              <div>
                <p className="text-sm font-medium">Please sign below to approve:</p>
                <canvas
                  ref={canvasRef}
                  width={360}
                  height={140}
                  className="mt-2 w-full touch-none rounded-xl border border-slate-300 bg-white"
                />
                <button
                  type="button"
                  className="mt-1 text-xs font-semibold text-slate-500"
                  onClick={() => {
                    const ctx = canvasRef.current?.getContext('2d');
                    if (ctx && canvasRef.current) {
                      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
                    }
                  }}
                >
                  Clear
                </button>
              </div>
            ) : null}

            <label className="block text-sm font-medium">
              Your name
              <input
                className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </label>

            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => declineMutation.mutate()}
                className="flex-1 rounded-xl border border-slate-300 px-4 py-3 text-sm font-semibold"
              >
                ✗ Decline Estimate
              </button>
              <button
                type="button"
                disabled={!name.trim() || approveMutation.isPending}
                onClick={() => approveMutation.mutate()}
                className="flex-1 rounded-xl bg-[#1B4F8A] px-4 py-3 text-sm font-semibold text-white disabled:opacity-50"
              >
                ✓ Approve & Authorize →
              </button>
            </div>
          </div>
        )}
      </div>
    </PublicShell>
  );
}
